import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Group, Title } from "@mantine/core";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { post } from "aws-amplify/api";
import CancelSubscriptionDialog from "@/components/Plans/CancelSubscriptionDialog";
import ManageSubscriptionDialog from "@/components/Plans/ManagerSubscriptionDialog";
import PlanFeatures from "@/components/Plans/PlanFeatures";
import SkeletonFeatures from "@/components/Plans/SkeletonFeatures";
import InvoiceModal from "@/components/Stripe/InvoiceModal";
import { useUserProvider } from "@/utils/UserProvider";


function Plans() {
  const userSelector = (context: any) => [context.user];
  const { user } = useAuthenticator(userSelector);
  const [searchParams] = useSearchParams();
  const [hideFreePlan, setHideFreePlan] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openedModalCancel, setOpenedModalCancel] = useState<string | null>(
    null,
  );
  const [openedModalManageSubscription, setOpenedModalManageSubscription] =
    useState(false);
  const { initRevcat, amplifyUser, revcatUser, revokeRevcatPromotion } =
    useUserProvider();
  const [userSubscriptions, setUserSubscriptions] = useState<any>([]);
  const [products, setProducts] = useState<any>([]);
  const [prices, setPrices] = useState<any>([]);
  const [activeStripeSubscription, setActiveStripeSubscription] =
    useState<any>(null);
  const [scheduledSubscriptions, setScheduledSubscriptions] = useState<any>([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState<any>(null);
  const [openedUpcomingInvoiceModal, setUpcomingInvoiceModal] = useState(false);
  const initializedStripe = useRef(false);

  useEffect(() => {
    const upgrade = searchParams.get("upgrade");
    if (upgrade == "true") {
      setHideFreePlan(true);
    }
  }, []);

  useEffect(() => {
    async function init() {
      const userSubscription = revcatUser?.userSubscription;
      if (
        userSubscription?.current_subscription &&
        userSubscription?.current_subscription?.store != "stripe" &&
        new Date(userSubscription?.current_subscription?.expires_at) >
          new Date()
      ) {
        setOpenedModalManageSubscription(true);
      } else {
        await initStripe();
      }
    }
    if (
      amplifyUser?.email &&
      revcatUser?.customerInfoApi &&
      !initializedStripe.current
    ) {
      initializedStripe.current = true;
      init();
    }
  }, [amplifyUser?.email, revcatUser?.customerInfoApi]);

  const userId = user.userId;

  const onCancelSubscription = async () => {
    const subId = activeStripeSubscription?.id;
    if (!subId) {
      return;
    }
    const postCancelSubscription = post({
      apiName: "StripeBackend",
      path: `/stripe/cancel-subscription`,
      options: {
        body: {
          subId,
          userId,
          store: "stripe",
        },
      },
    });
    try {
      const resp = await postCancelSubscription.response;
      await resp.body.json();
      await new Promise((resolve) => setTimeout(resolve, 2000));
      await initRevcat();
      await initStripe();
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const initStripe = async (silent: boolean = false) => {
    if (!silent) {
      setLoading(true);
    }
    const [subscriptions, products, prices] = await Promise.all([
      onGetListSubscriptions(),
      handleGetProducts(),
      handleGetPrices(),
    ]);

    setUserSubscriptions(subscriptions);
    setProducts(products);
    setPrices(prices);

    if (subscriptions.length > 0) {
      const activeStripeSubscription = subscriptions[0];
      if (activeStripeSubscription) {
        setActiveStripeSubscription(activeStripeSubscription);
        const scheduledSubscriptions = await handleGetScheduledSubscriptions(
          activeStripeSubscription.customer,
        );
        setScheduledSubscriptions(scheduledSubscriptions);
        if (
          scheduledSubscriptions.length > 0 ||
          (activeStripeSubscription.status === "active" &&
            !activeStripeSubscription.cancel_at_period_end) ||
          activeStripeSubscription.status === "trialing"
        ) {
          const upcomingInvoice = await handleGetUpcomingInvoice(
            activeStripeSubscription.customer,
          );
          if (upcomingInvoice?.amount_due > 0) {
            setUpcomingInvoice(upcomingInvoice);
          } else {
            setUpcomingInvoice(null);
          }
        } else {
          setUpcomingInvoice(null);
        }
      }
    } else {
      setActiveStripeSubscription(null);
      setScheduledSubscriptions([]);
      setUpcomingInvoice(null);
    }

    if (!silent) {
      setLoading(false);
    }
  };

  const onGetListSubscriptions = async () => {
    if (amplifyUser?.email) {
      try {
        const postGetSubscriptions = post({
          apiName: "StripeBackend",
          path: `/stripe/list-subscriptions`,
          options: {
            body: {
              email: amplifyUser.email,
            },
          },
        });

        const resp = await postGetSubscriptions.response;
        const result: any = await resp.body.json();

        if (
          result &&
          result?.subscriptions &&
          result?.subscriptions?.data.length > 0
        ) {
          return result?.subscriptions?.data?.filter(
            (item: any) => item.status !== "canceled",
          );
        }
      } catch (e) {
        console.log(e);
      }
    }
    return [];
  };

  const handleGetProducts = async () => {
    const postGetProducts = post({
      apiName: "StripeBackend",
      path: `/stripe/list-products`,
    });
    const resp = await postGetProducts.response;
    const result: any = await resp.body.json();
    if (
      result &&
      result?.products?.data &&
      result?.products?.data?.length > 0
    ) {
      return result?.products?.data;
    }
    return [];
  };

  const handleGetUpcomingInvoice = async (customerId: string) => {
    try {
      const postGetUpcomingInvoice = post({
        apiName: "StripeBackend",
        path: `/stripe/create-invoice-preview`,
        options: {
          body: {
            customerId,
          },
        },
      });
      const resp = await postGetUpcomingInvoice.response;
      const result: any = await resp.body.json();
      return result?.invoice;
    } catch (e) {
      console.log(e);
    }
    return null;
  };

  const handleGetPrices = async () => {
    const postGetPrices = post({
      apiName: "StripeBackend",
      path: `/stripe/list-prices`,
    });
    const resp = await postGetPrices.response;
    const result: any = await resp.body.json();
    if (result && result?.prices?.data && result?.prices?.data?.length > 0) {
      return result?.prices?.data;
    }
    return [];
  };

  const handleGetScheduledSubscriptions = async (customerId: string) => {
    try {
      const postGetScheduledSubscriptions = post({
        apiName: "StripeBackend",
        path: `/stripe/list-schedule-subscriptions`,
        options: {
          body: {
            customerId,
          },
        },
      });
      const resp = await postGetScheduledSubscriptions.response;
      const result: any = await resp.body.json();
      if (
        result &&
        result?.subscriptionSchedules?.data &&
        result?.subscriptionSchedules?.data?.length > 0
      ) {
        return result?.subscriptionSchedules?.data?.filter(
          (item: any) => item.status === "not_started",
        );
      }
    } catch (e) {
      console.log(e);
    }
    return [];
  };

  return (
    <Group
      id="checkout"
      style={{ justifyContent: "center", flexDirection: "column" }}
    >
      <Group pt={20} justify="center" pl={20} pr={20}>
        <Title style={{ textAlign: "center" }}>
          Please Select Your Subscription Type
        </Title>
      </Group>
      {loading && <SkeletonFeatures />}
      {!loading && (
        <PlanFeatures
          hideFreePlan={hideFreePlan}
          setOpenedModalCancel={(entitlement: string) => {
            setOpenedModalCancel(entitlement);
          }}
          activeStripeSubscription={activeStripeSubscription}
          scheduledSubscriptions={scheduledSubscriptions}
          userId={userId}
          amplifyUser={amplifyUser}
          products={products}
          prices={prices}
          initStripe={initStripe}
          initRevcat={initRevcat}
          revcatUser={revcatUser}
          userSubscriptions={userSubscriptions}
          upcomingInvoice={upcomingInvoice}
          setUpcomingInvoiceModal={setUpcomingInvoiceModal}
          revokeRevcatPromotion={revokeRevcatPromotion}
        />
      )}
      <CancelSubscriptionDialog
        openedModal={!!openedModalCancel}
        setOpenedModal={() => setOpenedModalCancel(null)}
        handleCancelSubscription={() => onCancelSubscription()}
      />
      <ManageSubscriptionDialog
        openedModal={openedModalManageSubscription}
        setOpenedModal={setOpenedModalManageSubscription}
      />
      {upcomingInvoice && (
        <InvoiceModal
          opened={openedUpcomingInvoiceModal}
          onClose={() => setUpcomingInvoiceModal(false)}
          invoiceData={upcomingInvoice}
          products={products}
        />
      )}
    </Group>
  );
}

export default Plans;
